import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, DollarSign, CreditCard, Mail, Phone, Loader2, Send, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_REASONS = [
  "Pizza party contribution",
  "Late pickup fee",
  "Field trip fee",
  "Supply fee",
  "Special event",
  "Other",
];

export default function ParentBillingPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    axios.get(`${API}/parent-communication/parent/billing`)
      .then((res) => {
        setData(res.data);
        setPayments(res.data.payments || []);
      })
      .catch(() => toast.error("Unable to load payment info"))
      .finally(() => setLoading(false));
  }, []);

  const finalReason = reason === "Other" ? customReason : reason;
  const canPay = parseFloat(amount) > 0 && finalReason.trim();

  const handlePayCard = async () => {
    if (!canPay) return;
    setGeneratingLink(true);
    try {
      const res = await axios.post(`${API}/parent-communication/parent/billing/pay-card`, {
        amount: parseFloat(amount),
        reason: finalReason.trim(),
      });
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to open card checkout");
    } finally {
      setGeneratingLink(false);
    }
  };

  const hasZelle = data?.zelleEmail || data?.zellePhone;
  const hasSquare = data?.squareEnabled;

  return (
    <div className="min-h-screen px-4 py-6" style={{ background: "radial-gradient(circle at top right, rgba(245,158,11,0.12), transparent 40%), radial-gradient(circle at bottom left, rgba(59,130,246,0.08), transparent 40%), #f6fbfa" }} data-testid="parent-billing-page">
      <div className="mx-auto max-w-lg space-y-5">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/parent")} className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200" data-testid="parent-billing-back-btn">
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Quick Pay</h1>
            <p className="text-sm text-slate-500">Send a payment to your center</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
        ) : (
          <>
            {/* Amount + Reason */}
            <div className="rounded-3xl bg-white shadow-lg border border-amber-100 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-4">
                <p className="text-white font-bold text-lg">How much would you like to send?</p>
                <p className="text-amber-50 text-xs mt-0.5">Payments go directly to your center</p>
              </div>
              <div className="p-5 space-y-4">
                {/* Amount */}
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-black text-slate-400">$</span>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="text-3xl font-black border-0 border-b-2 border-amber-300 rounded-none focus:border-amber-500 px-0 h-auto pb-1"
                    data-testid="parent-billing-amount-input"
                  />
                </div>

                {/* Reason pills */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-2">What's this for?</p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_REASONS.map((r) => (
                      <button
                        key={r}
                        onClick={() => setReason(r)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition ${reason === r ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"}`}
                        data-testid={`parent-billing-reason-${r.replace(/\s/g,"-").toLowerCase()}`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                  {reason === "Other" && (
                    <Textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="Describe the payment..."
                      rows={2}
                      className="mt-3 text-sm border-slate-300"
                      data-testid="parent-billing-custom-reason"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Payment options */}
            {!hasZelle && !hasSquare ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 px-4 py-5 text-center text-slate-400">
                <Info className="mx-auto h-6 w-6 mb-2 opacity-50" />
                <p className="text-sm">Your center hasn't set up payment options yet. Contact them directly.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">Choose how to pay</p>

                {hasZelle && (
                  <div className="rounded-3xl bg-white border border-blue-200 shadow-md overflow-hidden" data-testid="parent-billing-zelle-section">
                    <div className="bg-gradient-to-r from-blue-400 to-cyan-500 px-4 py-3 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-white" />
                      <p className="text-white font-bold">Pay via Zelle</p>
                    </div>
                    <div className="bg-blue-50 px-4 py-4 space-y-2">
                      <p className="text-xs text-slate-500">Open your bank app and send to:</p>
                      {data?.zelleEmail && (
                        <div className="flex items-center gap-2 rounded-xl bg-white border border-blue-200 px-3 py-2">
                          <Mail className="h-4 w-4 text-blue-400 flex-shrink-0" />
                          <p className="font-bold text-slate-800 text-sm">{data.zelleEmail}</p>
                        </div>
                      )}
                      {data?.zellePhone && (
                        <div className="flex items-center gap-2 rounded-xl bg-white border border-blue-200 px-3 py-2">
                          <Phone className="h-4 w-4 text-blue-400 flex-shrink-0" />
                          <p className="font-bold text-slate-800 text-sm">{data.zellePhone}</p>
                        </div>
                      )}
                      <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 mt-2">
                        <Info className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700">Include your name and reason in the Zelle memo. Your center will confirm receipt in the app.</p>
                      </div>
                    </div>
                  </div>
                )}

                {hasSquare && (
                  <div className="rounded-3xl bg-white border border-amber-200 shadow-md overflow-hidden" data-testid="parent-billing-card-section">
                    <div className="bg-gradient-to-r from-amber-400 to-orange-500 px-4 py-3 flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-white" />
                      <p className="text-white font-bold">Pay with Card</p>
                      <span className="ml-auto text-xs bg-white/20 text-white px-2 py-0.5 rounded-full font-semibold">Instant</span>
                    </div>
                    <div className="bg-amber-50 px-4 py-4">
                      <p className="text-xs text-slate-500 mb-3">Credit / Debit · Apple Pay · Google Pay — secure Square checkout</p>
                      <Button
                        onClick={handlePayCard}
                        disabled={!canPay || generatingLink}
                        className="w-full rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold py-3 shadow-md hover:opacity-90 transition disabled:opacity-50"
                        data-testid="parent-billing-pay-card-btn"
                      >
                        {generatingLink
                          ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening checkout...</>
                          : canPay
                            ? <><Send className="mr-2 h-4 w-4" /> Pay ${parseFloat(amount || 0).toFixed(2)} — {finalReason || "select a reason"}</>
                            : "Enter amount & reason above"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment history */}
            {payments.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">Payment History</p>
                {payments.map((p) => (
                  <div key={p.id} className="rounded-2xl bg-white border border-slate-200 px-4 py-3 flex items-center justify-between" data-testid={`parent-payment-history-${p.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{p.reason}</p>
                      <p className="text-xs text-slate-400">{new Date(p.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {p.method}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-black text-slate-800">${p.amount.toFixed(2)}</p>
                      <span className={`text-xs font-semibold ${p.status === "confirmed" ? "text-emerald-600" : "text-amber-600"}`}>
                        {p.status === "confirmed" ? "Confirmed" : "Pending"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
